import { type Group } from '@/shared/types/group';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui';
import { CampusSelect } from '../../common/input/campus';
import { useEffect, useState } from 'react';
import { GroupSelect } from '../../common/input/group';
import { UserMultiSelect } from '@/components/common/input/users';
import { Debug } from '../../common/output/debug';
import { getGroupAlias } from './utils';

export function LayGroupModal({
  isOpen,
  onOpenChange,
  target,
  mode,
  onCreate,
  onUpdate,
  saving,
}: {
  isOpen: boolean;
  onOpenChange: () => void;
  target: Group | null;
  mode: 'create' | 'update';
  onCreate?: (item: Group) => void;
  onUpdate?: (item: Group) => void;
  saving?: boolean;
}) {
  const [editable, setEditable] = useState<Group>(
    target || ({ id: '', data: {} } as Group),
  );

  const updateEditable = (data: Partial<Group['data']>) => {
    const updated: Group = {
      id: editable.id || target?.id || '',
      data: {
        ...target?.data,
        ...editable.data,
        ...data,
      },
    };
    setEditable(updated);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const value = target || ({ id: '', data: {} } as Group);
    console.debug('setting editable:', JSON.stringify(value));
    setEditable(value);
  }, [target, isOpen]);

  const onTitleChange = (value: string) => {
    updateEditable({
      title: value,
    });
  };

  const onCampusChange = (id: string) => {
    updateEditable({
      campus_id: id,
    });
  };

  const onParentChange = (id: string) => {
    updateEditable({
      parent_id: id,
    });
  };

  const onOversightChange = (ids: string[]) => {
    updateEditable({
      oversight_ids: ids,
    });
  };

  const onAssistantOversightChange = (ids: string[]) => {
    updateEditable({
      assistant_oversight_ids: ids,
    });
  };

  const finalizeHandler = () => {
    if (mode === 'create') {
      onCreate?.(editable);
    } else if (mode === 'update') {
      onUpdate?.(editable);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              {mode === 'create' ? 'New' : 'Edit'}{' '}
              {getGroupAlias(target?.data.type || 'laygroup1', 'type')}
            </ModalHeader>
            <ModalBody>
              {target ? (
                <>
                  <CampusSelect
                    label='Campus'
                    value={target.data.campus_id}
                    onChangeSingle={onCampusChange}
                  />
                  {target.data.type === 'laygroup2' && (
                    <GroupSelect
                      label={getGroupAlias('laygroup1', 'type')}
                      type={'laygroup1'}
                      campus={target.data.campus_id}
                      value={target.data.parent_id}
                      onChangeSingle={onParentChange}
                    />
                  )}
                  <UserMultiSelect
                    label={getGroupAlias(target.data.type, 'oversight')}
                    roles={['people', 'admin']}
                    status='unarchived'
                    initialSelected={target.data.oversight_ids}
                    onChange={onOversightChange}
                  />
                  <UserMultiSelect
                    label={`Assistant ${getGroupAlias(
                      target.data.type,
                      'oversight',
                    )}`}
                    roles={['people', 'admin']}
                    status='unarchived'
                    initialSelected={target.data.assistant_oversight_ids || []}
                    onChange={onAssistantOversightChange}
                  />
                  <Input
                    label='Title'
                    defaultValue={target.data.title || ''}
                    onValueChange={onTitleChange}
                  />
                  <Debug value={editable} />
                </>
              ) : (
                <div>no data</div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onPress={onClose} isDisabled={saving}>
                Cancel
              </Button>
              <Button
                color='primary'
                variant='solid'
                onPress={finalizeHandler}
                isLoading={saving}
              >
                {mode === 'create' ? 'Create' : 'Update'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
